import * as locales from "@mui/material/locale";
type MUILocaleKey = keyof typeof locales;

function isMUILocaleKey(value: string): value is MUILocaleKey {
  return value in locales;
}

function getLocaleKeysForLanguage(language: string): MUILocaleKey[] {
  return (Object.keys(locales) as MUILocaleKey[]).filter((key) =>
    key.toLowerCase().startsWith(language.toLowerCase()),
  );
}

// For langauges with multiple locale keys (like zhCN, zhTW, zhHK) we need to have a default.
const ambiguousLanguageDefaults: Partial<Record<string, MUILocaleKey>> = {
  ar: "arEG",
  ku: "kuCKB",
  nb: "nbNO",
  nn: "nnNO",
  pt: "ptBR",
  zh: "zhCN",
};

export function getMUILocale() {
  let locale: Intl.Locale;

  try {
    locale = new Intl.Locale(document.documentElement.lang || "en-US");
  } catch (e: unknown) {
    console.warn(
      `Invalid locale ${document.documentElement.lang} set on html element. Falling back to 'enUS' for MUI. Error:`,
      e,
    );
    return locales.enUS;
  }

  const { language, region, script } = locale;

  if (region) {
    const languageRegionKey = `${language}${region.toUpperCase()}`;
    if (isMUILocaleKey(languageRegionKey)) {
      return locales[languageRegionKey];
    }
  }

  if (script) {
    const languageScriptKey = `${language}${script}`;
    if (isMUILocaleKey(languageScriptKey)) {
      return locales[languageScriptKey];
    }
  }

  const matchingLocaleKeys = getLocaleKeysForLanguage(language);

  if (matchingLocaleKeys.length === 1) {
    return locales[matchingLocaleKeys[0]];
  }

  const amibiguoutDefault = ambiguousLanguageDefaults[language];
  if (amibiguoutDefault) {
    return locales[amibiguoutDefault];
  }

  if (matchingLocaleKeys.length > 1) {
    console.warn(
      `Multiple MUI locales found for language "${language}". Consider adding a more specific locale or updating the ambiguousLanguageDefaults map. Defaulting to ${matchingLocaleKeys[0]}.`,
    );
    return locales[matchingLocaleKeys[0]];
  }

  return locales.enUS; // default to English if we don't find a match
}
